import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Grid,
  Header,
  Input,
  Message,
  Modal
} from 'semantic-ui-react';
import CustomDatePicker from '../../../../components/CustomDatePicker';
import AppMaskedInput from '../../../../components/MaskedInput';
import { FieldLabel } from '../../../../styles';
import { DATE_INPUT_MASK, maskMoney } from '../../../../services/masks';
import { get } from '../../../../services/storage';
import { UserRolesEnum } from '../../../../enums/user-roles.enum';
import {
  dangerNotification,
  successNotification,
  warningNotification
} from '../../../../services/notification';
import { updateFireInsuranceInvoice } from '../../../../services/fire-insurance-invoices';
import {
  InvoicesStatusList,
  translateInvoiceStatus
} from '../../utils/AlfaSeguros';
import ConfirmInvoiceChangesModal from './ConfirmInvoiceChangesModal';
import { AdminEditInvoiceModalData, InvoiceFormState } from './types';
import {
  buildUpdatePayload,
  computeFieldDiffs,
  mapInvoiceToFormState,
  summarizeDiffs,
  validateForm
} from './utils';
import { MoneyInputWrapper, ReadOnlyGrid } from './styles';
import { Info } from '../../../../components/Info';

type AdminEditInvoiceModalProps = {
  open: boolean;
  close: () => void;
  data?: AdminEditInvoiceModalData;
  onSuccess: () => void;
};

const AdminEditInvoiceModal = ({
  open,
  close,
  data,
  onSuccess
}: AdminEditInvoiceModalProps) => {
  const userRole = get('role');
  const isBrokerAdmin = userRole === UserRolesEnum.brokerAdmin;

  const [form, setForm] = useState<InvoiceFormState | null>(null);
  const [original, setOriginal] = useState<InvoiceFormState | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open || !data) {
      return;
    }
    const mapped = mapInvoiceToFormState(data);
    setForm(mapped);
    setOriginal(mapped);
    setConfirmOpen(false);
  }, [open, data]);

  const diffs = useMemo(() => {
    if (!form || !original) {
      return [];
    }
    return computeFieldDiffs(original, form, translateInvoiceStatus);
  }, [form, original]);

  const hasChanges = diffs.length > 0;

  const updateField = <K extends keyof InvoiceFormState>(
    key: K,
    value: InvoiceFormState[K]
  ) => {
    setForm(prev => {
      if (!prev) {
        return prev;
      }
      const next = { ...prev, [key]: value };
      if (key === 'status' && value !== 'CLOSED') {
        next.paymentDate = null;
      }
      return next;
    });
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }
    setConfirmOpen(false);
    close();
  };

  const handleSaveClick = () => {
    if (!form || !original || !isBrokerAdmin) {
      return;
    }

    const validationError = validateForm(form);
    if (validationError) {
      warningNotification('Opss...', validationError);
      return;
    }

    if (!hasChanges) {
      warningNotification('Opss...', 'Nenhuma alteração para salvar.');
      return;
    }

    setConfirmOpen(true);
  };

  const handleConfirmSave = async () => {
    if (!form || !original) {
      return;
    }

    setIsLoading(true);
    try {
      await updateFireInsuranceInvoice(
        form.invoiceId,
        buildUpdatePayload(form, original)
      );
      successNotification(
        'Sucesso',
        `Fatura atualizada: ${summarizeDiffs(diffs)}.`
      );
      setConfirmOpen(false);
      onSuccess();
      close();
    } catch (error) {
      dangerNotification(
        'Erro',
        (error as any)?.message || 'Não foi possível atualizar a fatura.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        size="small"
        closeOnDimmerClick={!isLoading}
        closeOnEscape={!isLoading}
      >
        <Header icon="edit" content="Editar Fatura" />
        <Modal.Content>
          {!isBrokerAdmin && (
            <Message negative>
              Apenas administradores do corretor podem editar faturas por esta
              modal.
            </Message>
          )}

          <ReadOnlyGrid>
            <Grid>
              <Grid.Row>
                <Info name="Imobiliária" value={form?.estateName || ''} width={8} />
                <Info name="Seguradora" value={form?.insurerName || ''} width={8} />
              </Grid.Row>
              <Grid.Row>
                <Info
                  name="Código do contrato"
                  value={form?.contractCode || '—'}
                  width={5}
                />
                <Info
                  name="Código de emissão"
                  value={form?.emissionCode || '—'}
                  width={5}
                />
                <Info
                  name="Nº contribuição"
                  value={form?.contributionNumber || '—'}
                  width={6}
                />
              </Grid.Row>
            </Grid>
          </ReadOnlyGrid>

          <Form>
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <FieldLabel required>Valor total</FieldLabel>
                  <MoneyInputWrapper>
                    <Input
                      fluid
                      disabled={!isBrokerAdmin || isLoading}
                      value={form?.amount || ''}
                      onChange={(_, { value }) =>
                        updateField('amount', maskMoney(String(value)))
                      }
                    />
                  </MoneyInputWrapper>
                </Grid.Column>
                <Grid.Column width={8}>
                  <FieldLabel required>Status</FieldLabel>
                  <Dropdown
                    fluid
                    selection
                    disabled={!isBrokerAdmin || isLoading}
                    options={InvoicesStatusList}
                    value={form?.status || ''}
                    onChange={(_, { value }) =>
                      updateField('status', String(value || ''))
                    }
                  />
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <FieldLabel required>Data de vencimento</FieldLabel>
                  <CustomDatePicker
                    selected={form?.dueDate}
                    disabled={!isBrokerAdmin || isLoading}
                    onChange={(date: Date | null) => updateField('dueDate', date)}
                    dateFormat="dd/MM/yyyy"
                    customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
                  />
                </Grid.Column>
                <Grid.Column width={8}>
                  <FieldLabel required={form?.status === 'CLOSED'}>
                    Data de pagamento
                  </FieldLabel>
                  <CustomDatePicker
                    selected={form?.paymentDate}
                    disabled={
                      !isBrokerAdmin || isLoading || form?.status !== 'CLOSED'
                    }
                    onChange={(date: Date | null) =>
                      updateField('paymentDate', date)
                    }
                    dateFormat="dd/MM/yyyy"
                    customInput={<AppMaskedInput mask={DATE_INPUT_MASK} />}
                    isClearable
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button disabled={isLoading} onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            color="blue"
            loading={isLoading}
            disabled={isLoading || !isBrokerAdmin || !hasChanges}
            onClick={handleSaveClick}
          >
            Salvar alterações
          </Button>
        </Modal.Actions>
      </Modal>

      <ConfirmInvoiceChangesModal
        open={confirmOpen}
        diffs={diffs}
        isLoading={isLoading}
        onBack={() => setConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  );
};

export default AdminEditInvoiceModal;
export type { AdminEditInvoiceModalData };
